import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

// Fallback exchange rates relative to GBP (used if API fails)
const FALLBACK_RATES = {
  GBP: 1.00,
  USD: 1.27,
  EUR: 1.17,
  JPY: 189.50,
  AUD: 1.93,
  CAD: 1.72,
  CHF: 1.12,
  SEK: 13.25,
  NOK: 13.50,
  DKK: 8.72,
  PLN: 5.05,
};

const CURRENCY_SYMBOLS = {
  GBP: '£',
  USD: '$',
  EUR: '€',
  JPY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF',
  SEK: 'kr',
  NOK: 'kr',
  DKK: 'kr',
  PLN: 'zł',
};

const CurrencyContext = createContext();

// Cache duration: 1 hour
const CACHE_DURATION = 60 * 60 * 1000;

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrencyState] = useState(() => {
    try {
      const saved = localStorage.getItem('preferredCurrency');
      return saved && FALLBACK_RATES[saved] ? saved : 'GBP';
    } catch (e) {
      return 'GBP';
    }
  });

  const [exchangeRates, setExchangeRates] = useState(FALLBACK_RATES);
  const [ratesLoading, setRatesLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Fetch real-time exchange rates
  useEffect(() => {
    const fetchRates = async () => {
      try {
        // Check cache first
        const cached = localStorage.getItem('exchangeRatesCache');
        if (cached) {
          const { rates, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < CACHE_DURATION) {
            setExchangeRates(rates);
            setLastUpdated(new Date(timestamp));
            setRatesLoading(false);
            return;
          }
        }

        // Fetch from API - using fawazahmed0/exchange-api (free, no key required)
        const response = await axios.get(
          'https://cdn.jsdelivr.net/npm/@fawazahmed0/currency-api@latest/v1/currencies/gbp.json',
          { timeout: 5000 }
        );

        if (response.data && response.data.gbp) {
          const apiRates = response.data.gbp;
          
          // Map API response to our format (rates relative to GBP)
          const newRates = {
            GBP: 1.00,
            USD: apiRates.usd || FALLBACK_RATES.USD,
            EUR: apiRates.eur || FALLBACK_RATES.EUR,
            JPY: apiRates.jpy || FALLBACK_RATES.JPY,
            AUD: apiRates.aud || FALLBACK_RATES.AUD,
            CAD: apiRates.cad || FALLBACK_RATES.CAD,
            CHF: apiRates.chf || FALLBACK_RATES.CHF,
            SEK: apiRates.sek || FALLBACK_RATES.SEK,
            NOK: apiRates.nok || FALLBACK_RATES.NOK,
            DKK: apiRates.dkk || FALLBACK_RATES.DKK,
            PLN: apiRates.pln || FALLBACK_RATES.PLN,
          };

          setExchangeRates(newRates);
          setLastUpdated(new Date());
          
          // Cache the rates
          localStorage.setItem('exchangeRatesCache', JSON.stringify({
            rates: newRates,
            timestamp: Date.now()
          }));
        }
      } catch (error) {
        console.warn('Failed to fetch exchange rates, using fallback:', error.message);
        // Use fallback rates if API fails
        setExchangeRates(FALLBACK_RATES);
      } finally {
        setRatesLoading(false);
      }
    };

    fetchRates();
    
    // Refresh rates every hour
    const interval = setInterval(fetchRates, CACHE_DURATION);
    return () => clearInterval(interval);
  }, []);

  // Save currency preference
  const setCurrency = useCallback((newCurrency) => {
    setCurrencyState(newCurrency);
    try {
      localStorage.setItem('preferredCurrency', newCurrency);
    } catch (e) {}
  }, []);

  // Convert price from GBP to selected currency
  const convertPrice = useCallback((priceInGBP) => {
    if (typeof priceInGBP !== 'number' || isNaN(priceInGBP)) {
      return priceInGBP;
    }
    const rate = exchangeRates[currency] || 1;
    return priceInGBP * rate;
  }, [currency, exchangeRates]);

  // Format price with currency symbol
  const formatPrice = useCallback((priceInGBP) => {
    const converted = convertPrice(priceInGBP);
    const symbol = CURRENCY_SYMBOLS[currency] || '£';
    
    // Format based on currency
    if (currency === 'JPY') {
      // Yen doesn't use decimals
      return `${symbol}${Math.round(converted).toLocaleString()}`;
    }
    
    return `${symbol}${converted.toFixed(2)}`;
  }, [currency, convertPrice]);

  // Get just the symbol
  const getSymbol = useCallback(() => {
    return CURRENCY_SYMBOLS[currency] || '£';
  }, [currency]);

  const value = {
    currency,
    setCurrency,
    convertPrice,
    formatPrice,
    getSymbol,
    exchangeRates,
    symbols: CURRENCY_SYMBOLS,
    ratesLoading,
    lastUpdated,
    isLiveRates: lastUpdated !== null,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
};

export default CurrencyContext;
